import React from 'react';
import { useProcessingHistory } from '@/hooks/useProcessingHistory';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Undo2, Redo2, History, Trash2, Clock } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface HistoryPanelProps {
  sessionId: string;
  onRestoreImage?: (entry: any) => void;
}

export const HistoryPanel: React.FC<HistoryPanelProps> = ({ sessionId, onRestoreImage }) => {
  const {
    history,
    currentIndex,
    loading,
    canUndo,
    canRedo,
    undo,
    redo,
    goToHistoryPoint,
    clearHistory,
  } = useProcessingHistory(sessionId);

  const handleUndo = () => {
    const entry = undo();
    if (entry && onRestoreImage) {
      onRestoreImage(entry);
    }
  };

  const handleRedo = () => {
    const entry = redo();
    if (entry && onRestoreImage) {
      onRestoreImage(entry);
    }
  };

  const handleGoToPoint = (index: number) => {
    const entry = goToHistoryPoint(index);
    if (entry && onRestoreImage) {
      onRestoreImage(entry);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading history...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5" />
            Processing History
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleUndo}
              disabled={!canUndo}
            >
              <Undo2 className="h-4 w-4 mr-1" />
              Undo
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRedo}
              disabled={!canRedo}
            >
              <Redo2 className="h-4 w-4 mr-1" />
              Redo
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={clearHistory}
              disabled={history.length === 0}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No history yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Process images to see your history here
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {history.map((entry, index) => (
                <div
                  key={entry.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    index === currentIndex
                      ? 'bg-primary/10 border-primary'
                      : 'hover:bg-muted/50'
                  }`}
                  onClick={() => handleGoToPoint(index)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant={index === currentIndex ? 'default' : 'secondary'}>
                          #{index + 1}
                        </Badge>
                        <span className="font-medium">{entry.tool_used}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(entry.created_at).toLocaleString()}
                      </p>
                    </div>
                    {index === currentIndex && (
                      <Badge variant="outline">Current</Badge>
                    )}
                  </div>
                  {entry.settings && Object.keys(entry.settings).length > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      <details>
                        <summary className="cursor-pointer hover:text-foreground">
                          View settings
                        </summary>
                        <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                          {JSON.stringify(entry.settings, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};